import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { ScorecardData, AuthState, Metric, UserRole, OrgChartData, OrgChartPerson, GapData, GapItem } from '../types/scorecard';
import { enhancedScorecardData } from '../data/scorecardData3';
import { defaultGapData } from '../data/gapData';
import { exportScorecardToPDF } from '../utils/pdfExport';
import { LiveAppContext } from './LiveAppContext';

// Define default org chart data directly to avoid import issues
const defaultOrgChartData: OrgChartData = {
 people: [
  {
   "id": "ceo",
   "name": "CEO", 
   "title": "BZ",
   "isHired": true,
   "level": 0,
   "position": { "x": 500, "y": 0 },
   "lastUpdated": new Date("2026-02-22T14:25:09.088Z")
  },
  {
   "id": "cso",
   "name": "CSO",
   "title": "AS", 
   "isHired": true,
   "parentId": "ceo",
   "level": 1,
   "position": { "x": 0, "y": 150 },
   "lastUpdated": new Date("2026-02-22T14:25:13.351Z")
  }
 ],
 lastModified: new Date()
};

interface SimpleAppState {
 scorecard: ScorecardData;
 orgChart: OrgChartData;
 gapData: GapData;
 authState: AuthState;
 currentView: 'scorecard' | 'orgchart' | 'gap';
 previousState?: ScorecardData;
 previousOrgChart?: OrgChartData;
 previousGapData?: GapData;
 lastAction?: string;
 changeHistory: string[];
}

interface SimpleAppContextType {
 scorecard: ScorecardData;
 orgChart: OrgChartData;
 gapData: GapData;
 authState: AuthState;
 currentView: 'scorecard' | 'orgchart' | 'gap';
 updateMetric: (categoryId: string, level: number, metricId: string, updates: Partial<Metric>) => void;
 updateHierarchicalMetric: (categoryId: string, metricPath: string[], updates: Partial<Metric>) => void;
 addMetric: (categoryId: string, level: number, metric: Omit<Metric, 'id' | 'lastUpdated'>) => void;
 addHierarchicalMetric: (categoryId: string, parentPath: string[], metric: Omit<Metric, 'id' | 'lastUpdated'>) => void;
 removeMetric: (categoryId: string, level: number, metricId: string) => void;
 removeHierarchicalMetric: (categoryId: string, metricPath: string[]) => void;
 updateOrgChartPerson: (personId: string, updates: Partial<OrgChartPerson>) => void;
 addOrgChartPerson: (person: Omit<OrgChartPerson, 'id' | 'lastUpdated'>) => void;
 removeOrgChartPerson: (personId: string) => void;
 updateGapItem: (gapId: string, updates: Partial<GapItem>) => void;
 addGapItem: (gap: Omit<GapItem, 'id' | 'lastUpdated'>) => void;
 removeGapItem: (gapId: string) => void;
 setCurrentView: (view: 'scorecard' | 'orgchart' | 'gap') => void;
 undoLastAction: () => void;
 saveVersion: (changesSummary: string) => void;
 exportToPDF: (versionId?: string) => void;
 login: (password: string) => boolean;
 logout: () => void;
 canUndo: boolean;
}

type SimpleAppAction =
 | { type: 'UPDATE_METRIC'; payload: { categoryId: string; level: number; metricId: string; updates: Partial<Metric> } }
 | { type: 'ADD_METRIC'; payload: { categoryId: string; level: number; metric: Omit<Metric, 'id' | 'lastUpdated'> } }
 | { type: 'REMOVE_METRIC'; payload: { categoryId: string; level: number; metricId: string } }
 | { type: 'UPDATE_ORG_CHART_PERSON'; payload: { personId: string; updates: Partial<OrgChartPerson> } }
 | { type: 'ADD_ORG_CHART_PERSON'; payload: { person: Omit<OrgChartPerson, 'id' | 'lastUpdated'> } }
 | { type: 'REMOVE_ORG_CHART_PERSON'; payload: { personId: string } }
 | { type: 'UPDATE_GAP_ITEM'; payload: { gapId: string; updates: Partial<GapItem> } }
 | { type: 'ADD_GAP_ITEM'; payload: { gap: Omit<GapItem, 'id' | 'lastUpdated'> } }
 | { type: 'REMOVE_GAP_ITEM'; payload: { gapId: string } }
 | { type: 'SET_CURRENT_VIEW'; payload: { view: 'scorecard' | 'orgchart' | 'gap' } }
 | { type: 'UNDO_ACTION' }
 | { type: 'SAVE_VERSION'; payload: { changesSummary: string } }
 | { type: 'LOGIN'; payload: { user: { name: string; role: UserRole } } }
 | { type: 'LOGOUT' }
 | { type: 'LOAD_DATA'; payload: ScorecardData }
 | { type: 'LOAD_ORG_CHART'; payload: OrgChartData }
 | { type: 'LOAD_GAP_DATA'; payload: GapData };

const STORAGE_KEY = 'cardiovolt-scorecard';
const ORG_CHART_STORAGE_KEY = 'cardiovolt-orgchart';
const GAP_DATA_STORAGE_KEY = 'cardiovolt-gap-data';
const AUTH_KEY = 'cardiovolt-auth';
const ADMIN_PASSWORD = 'cardiovolt4i26';
const VIEWER_PASSWORD = 'cardiovolt26';

// Helper to recursively parse metrics with subMetrics
const parseMetric = (metric: any): any => ({
 ...metric,
 lastUpdated: new Date(metric.lastUpdated),
 subMetrics: metric.subMetrics?.map(parseMetric)
});

// Helper to parse dates in loaded data
const parseLoadedData = (data: any): ScorecardData => {
 return {
  ...data,
  lastModified: new Date(data.lastModified),
  versions: data.versions.map((v: any) => ({
   ...v,
   timestamp: new Date(v.timestamp),
   categories: v.categories.map((cat: any) => ({
    ...cat,
    levels: cat.levels.map((lvl: any) => ({
     ...lvl,
     metrics: lvl.metrics.map(parseMetric)
    }))
   }))
  })),
  currentVersion: data.currentVersion.map((category: any) => ({
   ...category,
   levels: category.levels.map((level: any) => ({
    ...level,
    metrics: level.metrics.map(parseMetric)
   }))
  }))
 };
};

const parseLoadedOrgChart = (data: any): OrgChartData => {
 return {
  ...data,
  lastModified: new Date(data.lastModified),
  people: data.people.map((person: any) => ({
   ...person,
   lastUpdated: new Date(person.lastUpdated)
  }))
 };
};

const parseLoadedGapData = (data: any): GapData => {
 return {
  ...data,
  lastModified: new Date(data.lastModified),
  gaps: data.gaps.map((gap: any) => ({
   ...gap,
   lastUpdated: new Date(gap.lastUpdated),
   // Migration: Convert old 'category' to new 'categories' array
   categories: gap.categories || (gap.category ? [gap.category] : ['Commercial'])
  }))
 };
};

export const getInitialState = (): SimpleAppState => {
 let scorecard = enhancedScorecardData;
 let orgChart = defaultOrgChartData;
 let gapData = parseLoadedGapData(defaultGapData);
 let authState: AuthState = { isAuthenticated: false };

 try {
  const savedScorecard = localStorage.getItem(STORAGE_KEY);
  if (savedScorecard) scorecard = parseLoadedData(JSON.parse(savedScorecard));

  const savedOrgChart = localStorage.getItem(ORG_CHART_STORAGE_KEY);
  if (savedOrgChart) orgChart = parseLoadedOrgChart(JSON.parse(savedOrgChart));

  const savedGapData = localStorage.getItem(GAP_DATA_STORAGE_KEY);
  if (savedGapData) gapData = parseLoadedGapData(JSON.parse(savedGapData));

  const savedAuth = localStorage.getItem(AUTH_KEY);
  if (savedAuth) authState = JSON.parse(savedAuth);
 } catch (e) {
  console.error('Error loading initial state:', e);
 }

 return {
  scorecard,
  orgChart,
  gapData,
  authState,
  currentView: 'scorecard',
  changeHistory: []
 };
};

const initialState = getInitialState();

function appReducer(state: SimpleAppState, action: SimpleAppAction): SimpleAppState {
 switch (action.type) {
  case 'UPDATE_METRIC': {
   const { categoryId, metricId, updates } = action.payload;
   
   // Recursive function to update metrics in hierarchical structure
   const updateMetricRecursive = (metrics: Metric[]): Metric[] => {
    return metrics.map(metric => {
     if (metric.id === metricId) {
      return {
       ...metric,
       ...updates,
       lastUpdated: new Date()
      };
     }
     // If this metric has subMetrics, check them recursively
     if (metric.subMetrics && metric.subMetrics.length > 0) {
      return {
       ...metric,
       subMetrics: updateMetricRecursive(metric.subMetrics)
      };
     }
     return metric;
    });
   };

   const updatedCategories = state.scorecard.currentVersion.map(category => {
    if (category.id === categoryId) {
     const updatedLevels = category.levels.map(lvl => {
      return {
       ...lvl,
       metrics: updateMetricRecursive(lvl.metrics)
      };
     });
     return { ...category, levels: updatedLevels };
    }
    return category;
   });

   const changeMessage = `Updated metric in ${categoryId}`;
   return {
    ...state,
    previousState: JSON.parse(JSON.stringify(state.scorecard)),
    lastAction: changeMessage,
    changeHistory: [changeMessage, ...state.changeHistory.slice(0, 9)], // Keep last 10 changes
    scorecard: {
     ...state.scorecard,
     currentVersion: updatedCategories,
     lastModified: new Date()
    }
   };
  }

  case 'ADD_METRIC': {
   const { categoryId, level, metric } = action.payload;
   const newMetric = {
    ...metric,
    id: `metric-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
    lastUpdated: new Date()
   };

   const updatedCategories = state.scorecard.currentVersion.map(category => {
    if (category.id === categoryId) {
     const updatedLevels = category.levels.map(lvl => {
      if (lvl.level === level) {
       return {
        ...lvl,
        metrics: [...lvl.metrics, newMetric]
       };
      }
      return lvl;
     });
     return { ...category, levels: updatedLevels };
    }
    return category;
   });

   const changeMessage = `Added metric "${metric.name}" to ${categoryId} Level ${level}`;
   return {
    ...state,
    previousState: JSON.parse(JSON.stringify(state.scorecard)),
    lastAction: changeMessage,
    changeHistory: [changeMessage, ...state.changeHistory.slice(0, 9)],
    scorecard: {
     ...state.scorecard,
     currentVersion: updatedCategories,
     lastModified: new Date()
    }
   };
  }

  case 'REMOVE_METRIC': {
   const { categoryId, level, metricId } = action.payload;
   const updatedCategories = state.scorecard.currentVersion.map(category => {
    if (category.id === categoryId) {
     const updatedLevels = category.levels.map(lvl => {
      if (lvl.level === level) {
       return {
        ...lvl,
        metrics: lvl.metrics.filter(metric => metric.id !== metricId)
       };
      }
      return lvl;
     });
     return { ...category, levels: updatedLevels };
    }
    return category;
   });

   // Find the metric name for the change message
   const category = state.scorecard.currentVersion.find(c => c.id === categoryId);
   const levelObj = category?.levels.find(l => l.level === level);
   const metric = levelObj?.metrics.find(m => m.id === metricId);
   const changeMessage = `Removed metric "${metric?.name || 'Unknown'}" from ${categoryId} Level ${level}`;

   return {
    ...state,
    previousState: JSON.parse(JSON.stringify(state.scorecard)),
    lastAction: changeMessage,
    changeHistory: [changeMessage, ...state.changeHistory.slice(0, 9)],
    scorecard: {
     ...state.scorecard,
     currentVersion: updatedCategories,
     lastModified: new Date()
    }
   };
  }

  case 'UPDATE_ORG_CHART_PERSON': {
   const { personId, updates } = action.payload;
   const updatedPeople = state.orgChart.people.map(person => {
    if (person.id === personId) {
     return {
      ...person,
      ...updates,
      lastUpdated: new Date()
     };
    }
    return person;
   });

   const changeMessage = `Updated person ${personId} in org chart`;
   return {
    ...state,
    previousOrgChart: JSON.parse(JSON.stringify(state.orgChart)),
    lastAction: changeMessage,
    changeHistory: [changeMessage, ...state.changeHistory.slice(0, 9)],
    orgChart: {
     ...state.orgChart,
     people: updatedPeople,
     lastModified: new Date()
    }
   };
  }

  case 'ADD_ORG_CHART_PERSON': {
   const { person } = action.payload;
   const newPerson = {
    ...person,
    id: `person-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
    lastUpdated: new Date()
   };

   const changeMessage = `Added person "${person.name}" to org chart`;
   return {
    ...state,
    previousOrgChart: JSON.parse(JSON.stringify(state.orgChart)),
    lastAction: changeMessage,
    changeHistory: [changeMessage, ...state.changeHistory.slice(0, 9)],
    orgChart: {
     ...state.orgChart,
     people: [...state.orgChart.people, newPerson],
     lastModified: new Date()
    }
   };
  }

  case 'REMOVE_ORG_CHART_PERSON': {
   const { personId } = action.payload;
   const person = state.orgChart.people.find(p => p.id === personId);
   const updatedPeople = state.orgChart.people.filter(p => p.id !== personId);

   const changeMessage = `Removed person "${person?.name || 'Unknown'}" from org chart`;
   return {
    ...state,
    previousOrgChart: JSON.parse(JSON.stringify(state.orgChart)),
    lastAction: changeMessage,
    changeHistory: [changeMessage, ...state.changeHistory.slice(0, 9)],
    orgChart: {
     ...state.orgChart,
     people: updatedPeople,
     lastModified: new Date()
    }
   };
  }

  case 'UPDATE_GAP_ITEM': {
   const { gapId, updates } = action.payload;
   const updatedGaps = state.gapData.gaps.map(gap => {
    if (gap.id === gapId) {
     return {
      ...gap,
      ...updates,
      lastUpdated: new Date()
     };
    }
    return gap;
   });

   const changeMessage = `Updated gap item ${gapId}`;
   return {
    ...state,
    previousGapData: JSON.parse(JSON.stringify(state.gapData)),
    lastAction: changeMessage,
    changeHistory: [changeMessage, ...state.changeHistory.slice(0, 9)],
    gapData: {
     ...state.gapData,
     gaps: updatedGaps,
     lastModified: new Date()
    }
   };
  }

  case 'ADD_GAP_ITEM': {
   const { gap } = action.payload;
   const newGap = {
    ...gap,
    id: `gap-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
    lastUpdated: new Date()
   };

   const changeMessage = `Added gap item "${gap.title}"`;
   return {
    ...state,
    previousGapData: JSON.parse(JSON.stringify(state.gapData)),
    lastAction: changeMessage,
    changeHistory: [changeMessage, ...state.changeHistory.slice(0, 9)],
    gapData: {
     ...state.gapData,
     gaps: [...state.gapData.gaps, newGap],
     lastModified: new Date()
    }
   };
  }

  case 'REMOVE_GAP_ITEM': {
   const { gapId } = action.payload;
   const gap = state.gapData.gaps.find(g => g.id === gapId);
   const updatedGaps = state.gapData.gaps.filter(g => g.id !== gapId);

   const changeMessage = `Removed gap item "${gap?.title || 'Unknown'}"`;
   return {
    ...state,
    previousGapData: JSON.parse(JSON.stringify(state.gapData)),
    lastAction: changeMessage,
    changeHistory: [changeMessage, ...state.changeHistory.slice(0, 9)],
    gapData: {
     ...state.gapData,
     gaps: updatedGaps,
     lastModified: new Date()
    }
   };
  }

  case 'SET_CURRENT_VIEW': {
   return {
    ...state,
    currentView: action.payload.view
   };
  }

  case 'UNDO_ACTION': {
   if (state.previousState) {
    const undoMessage = `Undid: ${state.lastAction || 'previous action'}`;
    return {
     ...state,
     scorecard: state.previousState,
     previousState: undefined,
     lastAction: undefined,
     changeHistory: [undoMessage, ...state.changeHistory.slice(0, 9)]
    };
   } else if (state.previousOrgChart) {
    const undoMessage = `Undid: ${state.lastAction || 'previous action'}`;
    return {
     ...state,
     orgChart: state.previousOrgChart,
     previousOrgChart: undefined,
     lastAction: undefined,
     changeHistory: [undoMessage, ...state.changeHistory.slice(0, 9)]
    };
   } else if (state.previousGapData) {
    const undoMessage = `Undid: ${state.lastAction || 'previous action'}`;
    return {
     ...state,
     gapData: state.previousGapData,
     previousGapData: undefined,
     lastAction: undefined,
     changeHistory: [undoMessage, ...state.changeHistory.slice(0, 9)]
    };
   }
   return state;
  }

  case 'SAVE_VERSION': {
   const newVersion = {
    id: `v${Date.now()}`,
    timestamp: new Date(),
    categories: JSON.parse(JSON.stringify(state.scorecard.currentVersion)),
    changesSummary: action.payload.changesSummary
   };

   return {
    ...state,
    scorecard: {
     ...state.scorecard,
     versions: [newVersion, ...state.scorecard.versions],
     lastModified: new Date()
    }
   };
  }

  case 'LOGIN': {
   return {
    ...state,
    authState: {
     isAuthenticated: true,
     user: action.payload.user
    }
   };
  }

  case 'LOGOUT': {
   return {
    ...state,
    authState: {
     isAuthenticated: false
    }
   };
  }

  case 'LOAD_DATA': {
   return {
    ...state,
    scorecard: action.payload
   };
  }

  case 'LOAD_ORG_CHART': {
   return {
    ...state,
    orgChart: action.payload
   };
  }

  case 'LOAD_GAP_DATA': {
   return {
    ...state,
    gapData: action.payload
   };
  }

  default:
   return state;
 }
}

export const SimpleAppContext = createContext<SimpleAppContextType | undefined>(undefined);

export function SimpleAppProvider({ children }: { children: React.ReactNode }) {
 const [state, dispatch] = useReducer(appReducer, initialState);

 // Save data to localStorage whenever state changes
 useEffect(() => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state.scorecard));
 }, [state.scorecard]);

 // Save org chart to localStorage whenever it changes
 useEffect(() => {
  localStorage.setItem(ORG_CHART_STORAGE_KEY, JSON.stringify(state.orgChart));
 }, [state.orgChart]);

 // Save gap data to localStorage whenever it changes
 useEffect(() => {
  localStorage.setItem(GAP_DATA_STORAGE_KEY, JSON.stringify(state.gapData));
 }, [state.gapData]);

 // Save auth state
 useEffect(() => {
  if (state.authState.isAuthenticated) {
   localStorage.setItem(AUTH_KEY, JSON.stringify(state.authState));
  } else {
   localStorage.removeItem(AUTH_KEY);
  }
 }, [state.authState]);

 const contextValue: SimpleAppContextType = {
  scorecard: state.scorecard,
  orgChart: state.orgChart,
  gapData: state.gapData,
  authState: state.authState,
  currentView: state.currentView,
  canUndo: !!state.previousState || !!state.previousOrgChart || !!state.previousGapData,

  updateMetric: (categoryId: string, level: number, metricId: string, updates: Partial<Metric>) => {
   dispatch({ type: 'UPDATE_METRIC', payload: { categoryId, level, metricId, updates } });
  },

  addMetric: (categoryId: string, level: number, metric: Omit<Metric, 'id' | 'lastUpdated'>) => {
   dispatch({ type: 'ADD_METRIC', payload: { categoryId, level, metric } });
  },

  removeMetric: (categoryId: string, level: number, metricId: string) => {
   dispatch({ type: 'REMOVE_METRIC', payload: { categoryId, level, metricId } });
  },

  updateHierarchicalMetric: (categoryId: string, metricPath: string[], updates: Partial<Metric>) => {
   // For now, route to the standard updateMetric function
   // Can be enhanced later for true hierarchical updates
   dispatch({ type: 'UPDATE_METRIC', payload: { categoryId, level: 1, metricId: metricPath[0], updates } });
  },

  addHierarchicalMetric: (categoryId: string, parentPath: string[], metric: Omit<Metric, 'id' | 'lastUpdated'>) => {
   // For now, route to the standard addMetric function
   dispatch({ type: 'ADD_METRIC', payload: { categoryId, level: 1, metric } });
  },

  removeHierarchicalMetric: (categoryId: string, metricPath: string[]) => {
   // For now, route to the standard removeMetric function
   dispatch({ type: 'REMOVE_METRIC', payload: { categoryId, level: 1, metricId: metricPath[0] } });
  },

  updateOrgChartPerson: (personId: string, updates: Partial<OrgChartPerson>) => {
   dispatch({ type: 'UPDATE_ORG_CHART_PERSON', payload: { personId, updates } });
  },

  addOrgChartPerson: (person: Omit<OrgChartPerson, 'id' | 'lastUpdated'>) => {
   dispatch({ type: 'ADD_ORG_CHART_PERSON', payload: { person } });
  },

  removeOrgChartPerson: (personId: string) => {
   dispatch({ type: 'REMOVE_ORG_CHART_PERSON', payload: { personId } });
  },

  updateGapItem: (gapId: string, updates: Partial<GapItem>) => {
   dispatch({ type: 'UPDATE_GAP_ITEM', payload: { gapId, updates } });
  },

  addGapItem: (gap: Omit<GapItem, 'id' | 'lastUpdated'>) => {
   dispatch({ type: 'ADD_GAP_ITEM', payload: { gap } });
  },

  removeGapItem: (gapId: string) => {
   dispatch({ type: 'REMOVE_GAP_ITEM', payload: { gapId } });
  },

  setCurrentView: (view: 'scorecard' | 'orgchart' | 'gap') => {
   dispatch({ type: 'SET_CURRENT_VIEW', payload: { view } });
  },

  undoLastAction: () => {
   dispatch({ type: 'UNDO_ACTION' });
  },

  saveVersion: (changesSummary: string) => {
   dispatch({ type: 'SAVE_VERSION', payload: { changesSummary } });
  },

  exportToPDF: (versionId?: string) => {
   const targetVersion = versionId
    ? state.scorecard.versions.find((v: any) => v.id === versionId)?.categories
    : state.scorecard.currentVersion;

   if (targetVersion) {
    exportScorecardToPDF(
     state.scorecard.title,
     state.scorecard.subtitle,
     targetVersion,
     versionId,
     state.changeHistory
    );
   }
  },

  login: (password: string) => {
   if (password === ADMIN_PASSWORD) {
    dispatch({
     type: 'LOGIN',
     payload: {
      user: {
       name: 'Admin',
       role: 'admin'
      }
     }
    });
    return true;
   } else if (password === VIEWER_PASSWORD) {
    dispatch({
     type: 'LOGIN',
     payload: {
      user: {
       name: 'Viewer',
       role: 'viewer'
      }
     }
    });
    return true;
   }
   return false;
  },

  logout: () => {
   dispatch({ type: 'LOGOUT' });
  }
 };

 return (
  <SimpleAppContext.Provider value={contextValue}>
   {children}
  </SimpleAppContext.Provider>
 );
}

export function useSimpleApp(): SimpleAppContextType {
 const context = useContext(SimpleAppContext);
 const liveContext = useContext(LiveAppContext as React.Context<any>);
 
 if (context !== undefined) {
  return context;
 }
 
 if (liveContext !== undefined) {
  // Provide an adapter for missing SimpleApp methods when used within Live context
  return {
   ...liveContext,
   undoLastAction: () => console.log('undo unsupported in live mode'),
   saveVersion: () => console.log('saveVersion unsupported in live mode'),
   exportToPDF: () => console.log('exportToPDF unsupported in live mode'),
   canUndo: false,
  } as any;
 }

 throw new Error('useSimpleApp must be used within a SimpleAppProvider or LiveAppProvider');
}